import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { ColumnDef, PaginationState, SortingState } from '@tanstack/react-table';
import { Plus, RefreshCw, Download, Search } from 'lucide-react';
import { DataTable } from '@/components/ui/data-table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ContractStatusBadge } from '@/components/ui/status-badge';
import { useContracts } from '../services/contractService';
import { useDepartments } from '../services/departmentService';
import { formatCurrency, formatCalendarDate } from '../utils/formatters';
import { calendarDay, todayCalendarDay } from '../utils/calendarDate';
import { formatCostPerUser, NOT_AVAILABLE } from '../utils/costPerUser';
import type { ContractListItem, ContractStatus } from '../types';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Contract List Page Component
 *
 * Displays a searchable, sortable, and filterable data table of all contracts
 * with server-side pagination, CSV export, and row click navigation.
 */
const ContractList: React.FC = () => {
  const navigate = useNavigate();
  const { canManageContracts } = useAuth();

  // The status filter lives in the URL (/contracts?status=...), so the dashboard tile, a pasted
  // link and browser Back/Forward all agree with the select. The other filters are local state.
  const { status } = useSearch({ from: '/contracts' });
  const statusFilter = status ?? 'all';
  const [search, setSearch] = React.useState('');
  const [departmentFilter, setDepartmentFilter] = React.useState<string>('all');
  const { data: departments } = useDepartments();
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'endDate', desc: false },
  ]);

  // Fetch contracts with current filters
  const { data, isLoading, error, refetch } = useContracts({
    search: search || undefined,
    status: statusFilter === 'all' ? undefined : (statusFilter as ContractStatus),
    departmentId: departmentFilter === 'all' ? undefined : departmentFilter,
    pageNumber: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
  });

  /**
   * Filters narrow the server-side result set, so every change starts again on page one
   * (the table is manually paginated and would otherwise request a page past the end).
   */
  const resetToFirstPage = () => setPagination((p) => ({ ...p, pageIndex: 0 }));

  // A status change that arrives through the URL (Back/Forward, a link) must also start on page
  // one; the table is manually paginated, so adjust the state when the prop changes.
  const [prevStatus, setPrevStatus] = React.useState(status);
  if (status !== prevStatus) {
    setPrevStatus(status);
    resetToFirstPage();
  }

  const handleSearchChange = (value: string) => {
    setSearch(value);
    resetToFirstPage();
  };

  const handleStatusChange = (value: string) => {
    navigate({ to: '/contracts', search: { status: value === 'all' ? undefined : (value as ContractStatus) } });
  };

  const handleDepartmentChange = (value: string) => {
    setDepartmentFilter(value);
    resetToFirstPage();
  };

  /**
   * Handle row click to navigate to contract detail
   */
  const handleRowClick = (row: ContractListItem) => {
    navigate({ to: `/contracts/${row.id}` });
  };

  /**
   * Handle create new contract button
   */
  const handleCreateContract = () => {
    navigate({ to: '/contracts/new' });
  };

  /**
   * Handle CSV export
   */
  const handleExport = () => {
    if (!data?.items || data.items.length === 0) return;

    const headers = ['Contract #', 'Title', 'Vendor', 'Owner', 'Department', 'Type', 'Status', 'Start Date', 'End Date', 'Notice Deadline', 'Annual Value', 'Seats', 'Cost per User'];
    const rows = data.items.map((contract) => [
      contract.contractNumber,
      contract.title,
      contract.vendorName,
      contract.ownerName ?? '',
      contract.departmentName ?? '',
      contract.contractType,
      contract.status,
      // Calendar days as YYYY-MM-DD (#65)
      calendarDay(contract.startDate),
      calendarDay(contract.endDate),
      calendarDay(contract.noticeDeadline),
      contract.annualContractValue,
      // Plain numbers like the annual value; n/a when the contract has no seat data (#17)
      contract.seatCount ?? NOT_AVAILABLE,
      contract.costPerUser === null || contract.costPerUser === undefined ? NOT_AVAILABLE : contract.costPerUser.toFixed(2),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contracts-${todayCalendarDay()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  /**
   * DataTable column definitions
   */
  const columns: ColumnDef<ContractListItem>[] = [
    {
      accessorKey: 'contractNumber',
      header: 'Contract #',
    },
    {
      accessorKey: 'title',
      header: 'Title',
    },
    {
      accessorKey: 'vendorName',
      header: 'Vendor',
    },
    {
      accessorKey: 'ownerName',
      header: 'Owner',
      cell: ({ row }) => row.original.ownerName ?? '\u2014',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <ContractStatusBadge status={row.original.status} />,
    },
    {
      accessorKey: 'contractType',
      header: 'Type',
    },
    {
      accessorKey: 'endDate',
      header: 'End Date',
      cell: ({ row }) => formatCalendarDate(row.original.endDate),
    },
    {
      accessorKey: 'noticeDeadline',
      header: 'Notice Deadline',
      cell: ({ row }) => (row.original.noticeDeadline ? formatCalendarDate(row.original.noticeDeadline) : '\u2014'),
    },
    {
      // The key doubles as the API sort field; 'annualValue' matched nothing and fell back to newest first
      accessorKey: 'annualContractValue',
      header: 'Annual Value',
      cell: ({ row }) => formatCurrency(row.original.annualContractValue),
    },
    {
      // Sorted on the server (sortBy=costPerUser); contracts without seat data come last
      accessorKey: 'costPerUser',
      header: 'Cost / User',
      cell: ({ row }) => formatCostPerUser(row.original.costPerUser),
    },
  ];

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-semibold text-grey-800">Contracts</h1>
        {canManageContracts && (
          <Button size="lg" onClick={handleCreateContract} aria-label="Create new contract">
            <Plus className="h-4 w-4" />
            New Contract
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-[2]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-grey-400" />
            <Input
              placeholder="Search by title, contract number, or vendor..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={statusFilter} onValueChange={handleStatusChange}>
            <SelectTrigger className="flex-1 min-w-[150px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="PendingRenewal">Pending Renewal</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Terminated">Terminated</SelectItem>
            </SelectContent>
          </Select>
          <Select value={departmentFilter} onValueChange={handleDepartmentChange}>
            <SelectTrigger className="flex-1 min-w-[150px]" aria-label="Filter by department">
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {/* Inactive departments stay listed: contracts keep their historical department */}
              {departments?.map((d) => (
                <SelectItem key={d.id} value={d.id}>
                  {d.name}
                  {d.isActive ? '' : ' (inactive)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>Failed to load contracts. Please try again later.</AlertDescription>
        </Alert>
      )}

      {/* Data Table */}
      <Card>
        <DataTable
          columns={columns}
          data={data?.items || []}
          rowCount={data?.totalCount || 0}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          onRowClick={handleRowClick}
          toolbar={
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          }
        />
      </Card>
    </div>
  );
};

export default ContractList;
